import { memo } from 'react';
import { Handle, Position, type NodeProps } from 'reactflow';
import { UserCheck } from 'lucide-react';

interface UserActionNodeData {
    label?: string;
    actions?: string; // Comma separated, e.g. "Eligible, Not Eligible"
    title?: string;
    description?: string;
    assignee?: string;
}

export const UserActionNode = memo(({ data, isConnectable, selected }: NodeProps<UserActionNodeData>) => {

    // Non-Blocking Mode: No decision options needed.

    return (
        <div className="relative flex flex-col items-center">
            <div className={`
                px-4 py-3 rounded-2xl shadow-sm border-[1.5px] bg-white min-w-[180px] max-w-[250px]
                ${selected ? 'border-rose-400 ring-4 ring-rose-400/10 shadow-xl scale-105' : 'border-rose-200 hover:border-rose-400 hover:shadow-md'}
                transition-all duration-300 relative group flex flex-col justify-center
            `}>
                {/* Input Handle */}
                <Handle
                    type="target"
                    position={Position.Left}
                    isConnectable={isConnectable}
                    className="!w-3 !h-3 !bg-transparent !border-0 flex items-center justify-center z-50"
                    style={{ left: -14 }}
                >
                    <div className="w-1.5 h-1.5 border-t-[2px] border-r-[2px] border-slate-400 rotate-45 transform -translate-x-0.5" />
                </Handle>

                <div className="flex flex-col gap-2 w-full">
                    <div className="flex items-center gap-3">
                        <div className={`
                            flex items-center justify-center
                        `}>
                            <UserCheck className={`h-6 w-6 ${selected ? 'text-rose-600' : 'text-rose-500'} transition-transform duration-300 group-hover:scale-110`} strokeWidth={1.5} />
                        </div>

                        <div className="flex flex-col overflow-hidden">
                            <span className="text-[10px] font-bold text-slate-700 uppercase tracking-wider truncate">
                                {data?.label || "USER ACTION"}
                            </span>
                            <span className="text-[9px] text-slate-500 truncate font-medium max-w-[140px]" title={data.assignee || "Unassigned"}>
                                {data.assignee || "Unassigned"}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Single Output Handle (Fire-and-Forget) */}
                <Handle
                    type="source"
                    position={Position.Right}
                    isConnectable={isConnectable}
                    className="!w-3 !h-3 !bg-transparent !border-0 flex items-center justify-center z-50"
                    style={{ right: -14 }}
                >
                    <div className={`w-1.5 h-1.5 border-t-[2px] border-r-[2px] ${selected ? 'border-rose-500' : 'border-rose-400'} rotate-45 transform -translate-x-0.5`} />
                </Handle>
            </div>

            {/* External Footer Label */}
            <div className="mt-1.5 max-w-[200px] text-center">
                <p className="text-[9px] text-slate-400 font-mono truncate leading-tight select-none" title={data.title}>
                    {data.title || ""}
                </p>
            </div>
        </div>
    );
});
